import React, {
  ReactNode,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { Layout } from 'antd'
import { Outlet, useLocation } from 'react-router-dom'
import { Header } from './header'
import { MenuComponent } from './menuV2'
import { ButtonScrollTop } from '../../ui/ButtonScrollTop'
import { ContentWrapper, LayoutWrapper, StyledContent, StyledLayout } from './styles'
import { normalizePath } from '../../utilits'

export interface IBreadcrumb {
  href?: string
  title: string
}

interface LayoutConfig {
  breadcrumbs?: IBreadcrumb[]
  pageTitle?: string
  isViewScrollButton?: boolean
  layoutRef?: any
}

interface LayoutConfigContextValue {
  config: LayoutConfig
  setConfig: (config: Partial<LayoutConfig>) => void
  resetConfig: () => void
}

const DEFAULT_CONFIG: LayoutConfig = {
  breadcrumbs: [],
  pageTitle: undefined,
  isViewScrollButton: false,
  layoutRef: undefined,
}

const areBreadcrumbsEqual = (a?: IBreadcrumb[], b?: IBreadcrumb[]) => {
  const first = a ?? []
  const second = b ?? []

  if (first.length !== second.length) {
    return false
  }

  for (let index = 0; index < first.length; index += 1) {
    const left = first[index]
    const right = second[index]

    if (left?.href !== right?.href || left?.title !== right?.title) {
      return false
    }
  }

  return true
}

const isConfigEqual = (prev: LayoutConfig, next: LayoutConfig) => {
  return (
    prev.pageTitle === next.pageTitle &&
    prev.isViewScrollButton === next.isViewScrollButton &&
    prev.layoutRef === next.layoutRef &&
    areBreadcrumbsEqual(prev.breadcrumbs, next.breadcrumbs)
  )
}

const LayoutConfigContext = createContext<LayoutConfigContextValue | null>(null)

const layoutStyle = {
  height: '100%',
  minHeight: '100vh',
}

const LayoutConfigProvider = ({ children }: { children: ReactNode }) => {
  const [config, setConfigState] = React.useState<LayoutConfig>(DEFAULT_CONFIG)

  const setConfig = useCallback((value: Partial<LayoutConfig>) => {
    setConfigState(prev => {
      const nextConfig: LayoutConfig = {
        ...prev,
        ...value,
        breadcrumbs: value.breadcrumbs ?? prev.breadcrumbs ?? [],
      }

      if (isConfigEqual(prev, nextConfig)) {
        return prev
      }

      return nextConfig
    })
  }, [])

  const resetConfig = useCallback(() => {
    setConfigState(prev => {
      if (isConfigEqual(prev, DEFAULT_CONFIG)) {
        return prev
      }

      return DEFAULT_CONFIG
    })
  }, [])

  const contextValue = useMemo(
    () => ({
      config,
      setConfig,
      resetConfig,
    }),
    [config, setConfig, resetConfig],
  )

  return (
    <LayoutConfigContext.Provider value={contextValue}>{children}</LayoutConfigContext.Provider>
  )
}

const useLayoutConfigContext = () => {
  return useContext(LayoutConfigContext)
}

const B2BLayoutShell = ({ config, children }: { config: LayoutConfig; children?: ReactNode }) => {
  const location = useLocation()
  const fallbackLayoutRef = useRef<HTMLDivElement>(null)
  const { breadcrumbs = [], pageTitle, isViewScrollButton, layoutRef } = config
  const resolvedLayoutRef = layoutRef ?? fallbackLayoutRef

  useEffect(() => {
    const storeSearchForPath = (path: string, search: string) => {
      if (!path) {
        return
      }

      const normalized = normalizePath(path)

      sessionStorage.setItem(`listParams:${normalized}`, search)

      if (normalized !== path) {
        sessionStorage.setItem(`listParams:${path}`, search)
      }
    }

    const normalizedPathname = normalizePath(location.pathname)
    const search = location.search || ''

    storeSearchForPath(location.pathname, search)

    if (normalizedPathname !== location.pathname) {
      storeSearchForPath(normalizedPathname, search)
    }

    breadcrumbs?.forEach(breadcrumb => {
      if (!breadcrumb?.href) {
        return
      }

      const normalizedHref = normalizePath(breadcrumb.href)
      if (normalizedHref === normalizedPathname) {
        storeSearchForPath(breadcrumb.href, search)
      }
    })
  }, [breadcrumbs, location.pathname, location.search])

  return (
    <LayoutWrapper ref={resolvedLayoutRef}>
      {isViewScrollButton && <ButtonScrollTop />}
      <Layout style={layoutStyle}>
        <MenuComponent />
        <StyledLayout className="site-layout">
          <Header pageTitle={pageTitle} breadcrumbs={breadcrumbs} />
          <StyledContent>
            <ContentWrapper>{children}</ContentWrapper>
          </StyledContent>
        </StyledLayout>
      </Layout>
    </LayoutWrapper>
  )
}

const LayoutOutletContent = () => {
  const context = useLayoutConfigContext()

  if (!context) {
    throw new Error('B2BLayoutOutlet must be used within LayoutConfigProvider')
  }

  return (
    <B2BLayoutShell config={context.config}>
      <Outlet />
    </B2BLayoutShell>
  )
}

export const B2BLayoutOutlet = memo(() => (
  <LayoutConfigProvider>
    <LayoutOutletContent />
  </LayoutConfigProvider>
))

interface TB2BLayoutProps {
  layoutRef?: any
  children?: ReactNode
  pageTitle?: string
  isViewScrollButton?: boolean
  breadcrumbs?: IBreadcrumb[]
}

const StandaloneB2BLayout = ({
  children,
  breadcrumbs = [],
  layoutRef,
  isViewScrollButton,
  pageTitle,
}: TB2BLayoutProps) => {
  const mergedConfig = useMemo(
    () => ({
      ...DEFAULT_CONFIG,
      breadcrumbs,
      layoutRef,
      isViewScrollButton,
      pageTitle,
    }),
    [breadcrumbs, isViewScrollButton, layoutRef, pageTitle],
  )

  return <B2BLayoutShell config={mergedConfig}>{children}</B2BLayoutShell>
}

const B2BLayout = ({
  children,
  breadcrumbs = [],
  layoutRef,
  isViewScrollButton,
  pageTitle,
}: TB2BLayoutProps) => {
  const context = useLayoutConfigContext()
  const contextRef = useRef(context)

  const memoizedConfig = useMemo(
    () => ({
      breadcrumbs,
      layoutRef,
      isViewScrollButton,
      pageTitle,
    }),
    [breadcrumbs, isViewScrollButton, layoutRef, pageTitle],
  )

  useEffect(() => {
    if (!context) {
      return
    }

    context.setConfig(memoizedConfig)
  }, [context, memoizedConfig])

  useEffect(() => {
    contextRef.current = context
  }, [context])

  useEffect(
    () => () => {
      contextRef.current?.resetConfig()
    },
    [],
  )

  if (!context) {
    return (
      <StandaloneB2BLayout
        breadcrumbs={breadcrumbs}
        layoutRef={layoutRef}
        isViewScrollButton={isViewScrollButton}
        pageTitle={pageTitle}
      >
        {children}
      </StandaloneB2BLayout>
    )
  }

  return <>{children}</>
}

export type { LayoutConfig }

export default B2BLayout
